/**
 * Phase 6E.2 — Daily Selective Challenge widget.
 * - Public visitors can answer.
 * - Logged-in students get +5 XP awarded server-side.
 * Embeddable on any landing/resource page.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, CheckCircle2, XCircle, Trophy, Loader2, ArrowRight, Lock } from 'lucide-react';
import QuestionVisual from './QuestionVisual';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const DailyChallengeWidget = () => {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [me, setMe] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [c, meR] = await Promise.all([
          ax.get('/api/public/daily-challenge'),
          ax.get('/api/auth/me').catch(() => ({ data: null })),
        ]);
        if (!live) return;
        setData(c.data);
        setMe(meR.data || null);
      } catch (_e) { if (live) setError('Could not load today\u2019s challenge'); }
    })();
    return () => { live = false; };
  }, []);

  const submit = async () => {
    if (selected == null || !data?.question?.id) return;
    setSubmitting(true);
    try {
      const r = await ax.post('/api/public/daily-challenge/submit', {
        question_id: data.question.id, answer_index: selected,
      });
      setResult(r.data);
      if (r.data?.xp_awarded > 0) {
        const { toast } = await import('sonner');
        toast.success(`+${r.data.xp_awarded} XP earned! 🎉`);
      }
      if (Array.isArray(r.data?.new_achievements) && r.data.new_achievements.length) {
        const { toast } = await import('sonner');
        r.data.new_achievements.forEach(a => {
          toast.success(`🏅 Unlocked: ${a.name}`, { duration: 5000 });
        });
      }
    } catch (_e) { setError('Submission failed — please retry'); }
    finally { setSubmitting(false); }
  };

  if (error) {
    return (
      <section className="bg-white border border-rose-200 rounded-2xl p-5 text-rose-800 text-sm" data-testid="dc-error">{error}</section>
    );
  }
  if (!data) {
    return (
      <section className="bg-white border border-gray-200 rounded-2xl p-5 flex items-center gap-2 text-gray-500 text-sm" data-testid="dc-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading today&apos;s challenge…
      </section>
    );
  }

  const q = data.question || {};
  const correct = result?.correct_answer_index;

  return (
    <section className="rounded-2xl border border-[#D3D86F]/40 bg-gradient-to-br from-black via-[#0a0a0a] to-black text-white p-6 shadow-lg relative overflow-hidden" data-testid="daily-challenge-widget">
      <div className="absolute inset-0 opacity-50 pointer-events-none" style={{
        background: 'radial-gradient(60% 50% at 0% 0%, rgba(220,38,38,0.30) 0%, rgba(220,38,38,0) 60%), radial-gradient(50% 40% at 100% 100%, rgba(211,216,111,0.18) 0%, rgba(211,216,111,0) 70%)',
      }} />
      <div className="relative">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#D3D86F] flex items-center gap-1.5">
              <Trophy className="h-3 w-3" /> Today&apos;s Selective Challenge
            </div>
            <h3 className="font-bold text-lg mt-0.5">{data.topic_title || 'Practice'}</h3>
            <div className="text-[11px] text-white/65 mt-0.5">
              {q.difficulty || 'Mixed'} · {q.estimated_minutes || 2} min · {q.track_slug?.replace('-', ' ') || 'Selective Entry'}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-white/50">Reward</div>
            <div className="text-base font-bold text-[#D3D86F] flex items-center gap-1 justify-end"><Sparkles className="h-3.5 w-3.5" />+5 XP</div>
          </div>
        </div>

        {(q.visual_type && q.visual_type !== 'none') && (
          <div className="bg-white/95 rounded-xl p-2 -mx-1">
            <QuestionVisual visual_type={q.visual_type} visual_payload={q.visual_payload}
              visual_caption={q.visual_caption} visual_alt_text={q.visual_alt_text} />
          </div>
        )}

        <p className="text-sm sm:text-base text-white/90 leading-relaxed whitespace-pre-line bg-white/5 border border-white/10 rounded-xl p-3" data-testid="dc-prompt">{q.prompt}</p>

        <div className="space-y-2 mt-3" data-testid="dc-choices">
          {(q.choices || []).map((c, i) => {
            const isSel = selected === i;
            const isCorrect = result && correct === i;
            const isWrong = result && isSel && correct !== i;
            return (
              <button key={i} onClick={() => !result && setSelected(i)} disabled={!!result}
                data-testid={`dc-choice-${i}`}
                className={`w-full text-left px-3 py-2.5 rounded-xl border transition flex items-start gap-2 text-sm ${
                  isCorrect ? 'bg-emerald-500/15 border-emerald-400 text-white'
                  : isWrong ? 'bg-rose-500/15 border-rose-400 text-white'
                  : isSel ? 'bg-[#D3D86F]/15 border-[#D3D86F] text-white'
                  : 'bg-white/5 border-white/10 hover:border-[#D3D86F]/40 text-white/85'
                }`}>
                <span className="text-[10px] uppercase tracking-wider text-white/50 mt-1 shrink-0 font-mono">{String.fromCharCode(65 + i)}</span>
                <span className="flex-1">{c}</span>
                {isCorrect && <CheckCircle2 className="h-4 w-4 text-emerald-300 shrink-0" />}
                {isWrong && <XCircle className="h-4 w-4 text-rose-300 shrink-0" />}
              </button>
            );
          })}
        </div>

        {!result ? (
          <button onClick={submit} disabled={selected == null || submitting}
            className="mt-4 w-full bg-[#D3D86F] hover:bg-[#c4ca5e] disabled:opacity-40 text-black py-3 rounded-full font-bold uppercase tracking-wider text-xs flex items-center justify-center gap-2"
            data-testid="dc-submit">
            {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trophy className="h-3.5 w-3.5" />}
            {submitting ? 'Submitting…' : 'Submit answer'}
          </button>
        ) : (
          <div className="mt-4 space-y-3" data-testid="dc-result">
            <div className={`rounded-xl px-3 py-2 text-sm font-bold ${result.is_correct ? 'bg-emerald-500/15 text-emerald-200 border border-emerald-400/40' : 'bg-rose-500/15 text-rose-200 border border-rose-400/40'}`}>
              {result.is_correct ? '🎉 Correct!' : '❌ Not quite — the correct answer was highlighted above.'}
              {result.xp_awarded > 0 && <span className="ml-2 text-[#D3D86F]">· +{result.xp_awarded} XP earned</span>}
            </div>
            {result.explanation && (
              <div className="bg-white/5 border border-white/10 rounded-xl p-3 text-sm text-white/85" data-testid="dc-explanation">
                <div className="text-[10px] uppercase tracking-wider text-[#D3D86F] font-bold mb-1">Explanation</div>
                {result.explanation}
              </div>
            )}
            {!me && (
              <div className="bg-white/5 border border-white/10 rounded-xl p-3 text-sm text-white/85" data-testid="dc-cta-anon">
                <div className="font-bold mb-1.5">Want unlimited practice?</div>
                <div className="flex flex-wrap gap-2 mt-2">
                  <Link to="/student/signup?program=selective-entry" data-testid="dc-cta-signup"
                    className="text-[11px] bg-[#D3D86F] hover:bg-[#c4ca5e] text-black px-4 py-2 rounded-full font-bold uppercase tracking-wider inline-flex items-center gap-1">
                    Create Student Account <ArrowRight className="h-3 w-3" />
                  </Link>
                  <Link to="/student/login" data-testid="dc-cta-login"
                    className="text-[11px] bg-white/10 hover:bg-white/20 px-4 py-2 rounded-full font-bold uppercase tracking-wider inline-flex items-center gap-1">
                    Student Login
                  </Link>
                  <Link to="/get-started?topic=selective-entry" data-testid="dc-cta-book"
                    className="text-[11px] border border-white/20 hover:bg-white/10 px-4 py-2 rounded-full font-bold uppercase tracking-wider inline-flex items-center gap-1">
                    Book Free Assessment
                  </Link>
                </div>
              </div>
            )}
            {me && !result.xp_awarded && (
              <div className="text-[11px] text-white/65 italic" data-testid="dc-already-done">
                <Lock className="inline h-3 w-3 mr-1" /> Already completed today — come back tomorrow for the next challenge.
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default DailyChallengeWidget;
